import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Link from 'next/link';

import ExternalLink from '../components/ExternalLink';
import { getInputClasses, helpText } from '../components/form-fields/common';
import useFocusOnError from '../components/hooks/useFocusOnError';
import { retrieveCSRFToken } from '../utils/ajax';
import useUser from '@/components/useUser';
import { useRouter } from 'next/router';

const formSchema = Yup.object().shape({
  username: Yup.string()
    .matches(/^[a-zA-Z0-9_-]+$/, 'Please only use letters, numbers, underscores, and hyphens')
    .min(2, 'Too Short!')
    .max(50, 'Too Long!')
    .required('Required'),
  email: Yup.string()
    .strict()
    .trim('You have extra spaces')
    .email('Invalid email')
    .required('Required'),
  password1: Yup.string()
    .min(2, 'Too Short!')
    .required('Required'),
  password2: Yup.string()
    .oneOf([Yup.ref('password1'), null], 'Passwords must match')
    .required('Required'),
  isOrganizationUser: Yup.boolean(),
  readTermsAndConditions: Yup.boolean()
    .oneOf([true], 'Please confirm you have read the Terms of Service'),
  isAgeOfMajority: Yup.boolean()
    .oneOf([true], 'Please confirm you are over the age of majority'),
});

const Signup = () => {
  const [disableSignup, setDisableSignup] = useState(false);
  const { user, refreshUser } = useUser();
  const router = useRouter();

  useEffect(() => {
    (async () => {

      const countReq = fetch('/api/members/config/max_user_count/');
      const usersReq = await fetch('/api/users/').then(res => res.json());
      const countRes = await countReq;

      if (!countRes.ok) return

      let { config_value: maxUserCount } = await countRes.json();

      const { count: userCount } = usersReq;

      maxUserCount = parseInt(maxUserCount);

      if (userCount >= maxUserCount) {
        console.log(`### disabling signup because ${userCount} >= ${maxUserCount}`);
        setDisableSignup(true);
      }
    })();
  }, []);

  const register = async (params, { setSubmitting, setErrors }) => {
    setSubmitting(true);
    try {
      const result = await fetch('/api-auth/registration/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      });

      const body = await result.json();

      if (result.status === 400) {
        const errors = Object.keys(body).reduce((acc, key) => {
          return { ...acc, [key]: body[key].join(" ") };
        }, {});

        setErrors(errors);
      } else if (result.status === 201) {
        if (params.isOrganizationUser) {
          await fetch('/api-auth/user/', {
            method: 'PATCH',
            headers: {
              'X-CSRFToken': retrieveCSRFToken(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ is_organization_user: true })
          });
        }

        toast.success(`welcome to thrivespring ${params.username}!`)
        refreshUser();
      } else {
        console.error('unhandled case...');
      }
    } finally {
      setSubmitting(false);
    }
  }
  const formik = useFormik({
    initialValues: {
      // email: `nada${Math.floor(Math.random() * 100)}@baba.ca`,
      // username: `nada${Math.floor(Math.random() * 100)}`,
      // password1: '123qwe!@#',
      // password2: '123qwe!@#',
      email: '',
      username: '',
      password1: '',
      password2: '',
      readTermsAndConditions: false,
      isAgeOfMajority: false,
      isOrganizationUser: false,
    },
    onSubmit: register,
    validationSchema: formSchema
  });

  useFocusOnError(formik);

  if (user) {
    const path = new URLSearchParams(window.location.search).get('next') || '/profile';
    router.push(path);
  }

  if (disableSignup) {
    return (
      <section className="section">
        <div className="container has-text-centered">
          <div className="box">
            <h1 className="title">
              Sorry! We are not accepting any more sign ups right now.
            </h1>

            <p>We are currently not accepting any more signups right now. But we will be expanding the platform soon!</p>
            <p className="block">If you sign up for our <a href="https://form.jotform.com/211018230360236" rel="noopener noreferrer" target="_blank">mailing list here</a> we will reach out to you as soon as possible!</p>
            <p><strong>Thank you</strong> for your interest in Thrivespring!</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section">
      <div className="columns">
        <div className="column is-three-fifths">
          <div className="content notification is-info" style={{height: '100%'}}>
            <h1 className="title pt-3">Before you sign up!</h1>

            <p>We are currently limiting sign ups to users in the <strong>United States</strong>, <strong>United Kingdom</strong>, and <strong>Canada</strong> to start. If you are from another country, please <ExternalLink newPage url="https://form.jotform.com/211018230360236">register your details in this form</ExternalLink> and we will let you know as soon as you are able to sign up.</p>

            <p>Please read these tips for a secure password:</p>

            <ul>
              <li><strong>Create a unique password</strong>. Using the same password that you use for your email account or online banking is never a good idea because if hackers get hold of your password through this site, they can then access your other accounts.</li>
              <li><strong>Use a password manager</strong> such as LastPass to create a long, random password, or create an easy to remember passphrase consisting of four or five random words. More details can be found <ExternalLink newPage url="https://protonmail.com/blog/protonmail-com-blog-password-vs-passphrase/">here</ExternalLink>.</li>
            </ul>

            <p><Link href="/your-security">Our Security page</Link> has additional tips on how to stay safe.</p>

            <p>And please be sure to read our <Link href="/purpose-and-guidelines">Purpose and Community Guidelines</Link>.</p>
          </div>
        </div>

        <div className="column">
          <div className="_box px-6">
            <form onSubmit={formik.handleSubmit}>
              <div className="field">
                <label className="label" htmlFor="username">Username</label>
                <div className="control">
                  <input
                    className={getInputClasses(formik, 'username')}
                    id="username"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.username} />
                </div>
                {helpText(formik, 'username')}
              </div>

              <div className="field">
                <label className="label" htmlFor="email">Email</label>
                <div className="control">
                  <input
                    className={getInputClasses(formik, 'email')}
                    id="email"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email} />
                </div>
                {helpText(formik, 'email')}
              </div>

              <div className="field">
                <label className="label" htmlFor="password1">Password</label>
                <div className="control">
                  <input
                    className={getInputClasses(formik, 'password1')}
                    id="password1"
                    type="password"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password1} />
                </div>
                {helpText(formik, 'password1') || <p className="help">
                  Your password can’t be too similar to your username. Your password must contain at least 8 characters. Your password can’t be a commonly used password. Your password can’t be entirely numeric.
                </p>}
              </div>

              <div className="field">
                <label className="label" htmlFor="password2">Confirm Password</label>
                <div className="control">
                  <input
                    className={getInputClasses(formik, 'password2')}
                    id="password2"
                    type="password"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password2} />
                </div>
                {helpText(formik, 'password2')}
              </div>

              <div className="block">
                <h2 className="subtitle">For Organizations</h2>

                <div className="field">
                  <label className="checkbox" id="isOrganizationUser">
                    <input
                      type="checkbox"
                      id="isOrganizationUser"
                      onChange={formik.handleChange}
                      checked={formik.values.isOrganizationUser} />
                    {' This is an Organization account'}
                  </label>
                  <p className="help">Signing up as an Organization gives you additional features.</p>
                </div>
              </div>

              <div className="block">
                <h2 className="subtitle">Legal Terms</h2>

                <div className="field">
                  <label className="checkbox" id="readTermsAndConditions">
                    <input
                      type="checkbox"
                      id="readTermsAndConditions"
                      onChange={formik.handleChange}
                      checked={formik.values.readTermsAndConditions} />
                    {' I agree to the '}<Link href="/terms-of-service">Terms of Service</Link>
                  </label>
                  {helpText(formik, 'readTermsAndConditions')}
                </div>

                <div className="field">
                  <label className="checkbox" id="isAgeOfMajority">
                    <input
                      type="checkbox"
                      id="isAgeOfMajority"
                      onChange={formik.handleChange}
                      checked={formik.values.isAgeOfMajority} />
                    {' I am over the age of majority in my jurisdiction'}
                  </label>
                  {helpText(formik, 'isAgeOfMajority')}
                </div>
              </div>

              <button
                type="submit"
                className="button is-success is-fullwidth"
                disabled={formik.isSubmitting}
              >
                {`Sign-Up as an ${formik.values.isOrganizationUser ? 'Organization' : 'Individual'}`}
              </button>

              <div className="has-text-centered mt-5">
                <small>Already have an account? <Link href={'/login'}>Login</Link></small>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Signup;
